import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import AutoRedirect from '../components/Autologin';
import { isMobile } from 'react-device-detect';
import { isInMigrationWindow, MIGRATION_MESSAGE } from '../utils/migrationUtils';
import OptIn from '../components/Optin';
import BrevoConsent from '../components/BrevoConsent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, /* faStore, */ faEnvelope, faNewspaper } from '@fortawesome/free-solid-svg-icons';

const Home = () => {
    const user = useSelector((state) => state.user.user);
    const sessionToken = useSelector((state) => state.user.token);
    const isLoggedIn = sessionToken !== null;
    const [showModal, setShowModal] = useState(false);
    const [showConsentModal, setShowConsentModal] = useState(false);
    const [showMigrationModal, setShowMigrationModal] = useState(false);
    const [pendingToMobileApp, setPendingToMobileApp] = useState(null);
    const [error, setError] = useState(null);

    const optInStatus = user?.user?.optin;
    const username = user?.user?.username;
    const firstname = user?.user?.firstname;
    const userId = user?.user?.id;
    const apiPrefix = process.env.REACT_APP_API_PREFIX;

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        if (token && !isMobile) {
            const newUrl = `${window.location.origin}${window.location.pathname}/${token}`;
            window.history.replaceState(null, null, newUrl);
        }
    }, []);

    // Afficher la modale de consentement après 3 secondes si l'utilisateur est connecté et pas encore abonné
    useEffect(() => {
        if (isLoggedIn && optInStatus === false) {
            const timer = setTimeout(() => {
                setShowConsentModal(true);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [isLoggedIn, optInStatus]);

    const doRedirect = async (toMobileApp) => {
        try {
            const response = await fetch(`${apiPrefix}autologin/token/${userId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${sessionToken}`,
                },
            });

            if (!response.ok) throw new Error('Impossible de récupérer le token');
            const data = await response.json();
            const token = data.token;

            if (toMobileApp) {
                window.location.href = `https://app.reducce.fr/autologin/${token}?organisation=CA-CEST-POUR-MOI`;
            } else {
                window.location.href = `http://ca-cpourmoi.reducce.fr/autologin/${token}`;
            }
        } catch (err) {
            console.error(err);
            setError("Erreur lors de la redirection. Veuillez réessayer.");
        }
    };

    const handleRedirect = async (toMobileApp) => {
        if (isInMigrationWindow()) {
            setPendingToMobileApp(toMobileApp);
            setShowMigrationModal(true);
        } else {
            await doRedirect(toMobileApp);
        }
    };

    const handleConfirmMigrationRedirect = async () => {
        if (pendingToMobileApp !== null) {
            await doRedirect(pendingToMobileApp);
            setPendingToMobileApp(null);
            setShowMigrationModal(false);
        }
    };

    const handleCancelMigrationRedirect = () => {
        setPendingToMobileApp(null);
        setShowMigrationModal(false);
    };

    const redirectUrl = !isMobile ? "http://ca-cpourmoi.reducce.fr/autologin/" : null;

    const handleConsentAccept = () => {
        setShowConsentModal(false);
        // Ici vous pouvez ajouter la logique pour inscrire l'utilisateur à la newsletter
        console.log('Utilisateur accepte l\'inscription à la newsletter');
    };

    const handleConsentDecline = () => {
        setShowConsentModal(false);
        console.log('Utilisateur refuse l\'inscription à la newsletter');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
            <div className="container-responsive section-padding">
                <div className="text-center max-w-4xl mx-auto">
                    {isLoggedIn && <h2 className="text-2xl font-bold text-primary mb-4">Bonjour {firstname}</h2>}
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 leading-tight">
                        Bienvenue sur votre <br /> 
                        <span className="text-primary">Comité d'entreprise</span> !
                    </h1>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        Découvrez des offres exclusives sur vos achats du quotidien.
                    </p>

                    {!isMobile && redirectUrl && <AutoRedirect redirectUrl={redirectUrl} />}

                    {isMobile && (
                        <button
                            onClick={() => setShowModal(true)}
                            className="mt-4 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition"
                        >
                            Accéder à mes offres
                        </button>
                    )}

                    {showModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-2xl p-6 shadow-xl max-w-md w-full text-center relative">
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
                                >
                                    &times;
                                </button>
                                <h2 className="text-xl font-semibold mb-4">Où souhaitez-vous aller ?</h2>
                                <p className="mb-6">Souhaitez-vous utiliser l'application mobile ou la version web ?</p>
                                <div className="flex justify-center gap-4">
                                    <button
                                        onClick={() => {
                                            setShowModal(false);
                                            handleRedirect(true);
                                        }}
                                        className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 transition"
                                    >
                                        Application mobile
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowModal(false);
                                            handleRedirect(false);
                                        }}
                                        className="bg-gray-300 text-gray-800 px-4 py-2 rounded-xl hover:bg-gray-400 transition"
                                    >
                                        Version web
                                    </button>
                                </div>
                                {error && <p className="text-red-500 mt-4">{error}</p>}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modale d'avertissement migration */}
            {showMigrationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-6 shadow-xl max-w-md w-full text-center relative mx-4">
                        <button
                            onClick={handleCancelMigrationRedirect}
                            className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
                        >
                            &times;
                        </button>
                        <div className="mb-4">
                            <i className="fas fa-info-circle text-4xl text-amber-500 mb-3"></i>
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">Information</h3>
                            <p className="text-gray-600">{MIGRATION_MESSAGE}</p>
                        </div>
                        <button
                            onClick={handleConfirmMigrationRedirect}
                            className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 transition"
                        >
                            Continuer
                        </button>
                    </div>
                </div>
            )}

            {/* Modale de consentement Brevo */}
            {showConsentModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <BrevoConsent 
                        onAccept={handleConsentAccept}
                        onDecline={handleConsentDecline}
                    />
                </div>
            )}

            {/* Cartes de services */}
            <div className="container-responsive pb-12">
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                    <div className="card hover:shadow-xl transition-shadow duration-300 flex flex-col h-full">
                        <h3 className="flex items-center text-xl font-bold text-gray-800 mb-4">
                            <span className="icon mr-3 text-primary">
                                <FontAwesomeIcon icon={faUser} />
                            </span>
                            Mon compte
                        </h3>
                        <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                            Bienvenue dans votre espace personnel. Ici, vous pouvez gérer vos informations personnelles et les mettre à jour, de manière simple et sécurisée.
                        </p>
                        <div className="mt-auto">
                            <Link to="/Profil">
                                <button className="btn-primary w-full">Mon compte</button>
                            </Link>
                        </div>
                    </div>
                    {/* <div className="card hover:shadow-xl transition-shadow duration-300 flex flex-col h-full">
                        <h3 className="flex items-center text-xl font-bold text-gray-800 mb-4">
                            <span className="icon mr-3 text-primary">
                                <FontAwesomeIcon icon={faStore} />
                            </span>
                            Référencer une enseigne
                        </h3>
                        <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                            Vous ne trouvez pas une enseigne que vous aimez ? Faites-nous part de vos suggestions ! Nous la contacterons pour l'ajouter à nos offres CE.
                        </p>
                        <div className="mt-auto">
                            <Link to="/ContactForm">
                                <button className="btn-primary w-full">Remplir le formulaire</button>
                            </Link>
                        </div>
                    </div> */}
                    <div className="card hover:shadow-xl transition-shadow duration-300 flex flex-col h-full">
                        <h3 className="flex items-center text-xl font-bold text-gray-800 mb-4">
                            <span className="icon mr-3 text-primary">
                                <FontAwesomeIcon icon={faEnvelope} />
                            </span>
                            Contact
                        </h3>
                        <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                            Pour toute réclamation sur la billetterie ou le paiement, contactez Ski Loisirs Diffusion au
                            <br /> 04 72 40 28 28 ou par mail à serviceclient@reducce.fr. Pour toute autre demande :
                        </p>
                        <div className="mt-auto">
                            <a href="mailto:contact@ca-cpourmoi.com">
                                <button className="btn-primary w-full">Contactez nous !</button>
                            </a>
                        </div>
                    </div>
                    <div className="card hover:shadow-xl transition-shadow duration-300 flex flex-col h-full">
                        <h3 className="flex items-center text-xl font-bold text-gray-800 mb-4">
                            <span className="icon mr-3 text-primary">
                                <FontAwesomeIcon icon={faNewspaper} />
                            </span>
                            Newsletter
                        </h3>
                        <p className="text-gray-600 mb-6 leading-relaxed">
                            Ne manquez rien ! Inscrivez-vous pour recevoir les bons plans de la semaine et des offres exclusives directement dans votre boîte mail.
                        </p>
                        <div className="mt-auto">
                            {optInStatus !== undefined && (
                                <div className="w-full">
                                    <OptIn username={username} optIn={optInStatus} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Home;